import { jsx } from '@emotion/react';
import { Component } from 'react';
import { labelStyles, withModifier } from '@zapier/style-encapsulation';
import { I as Icons } from '../../../index-DRG3ZeD1.mjs';
import { Animation } from '../../../theme/Animation.es.js';
import { Colors } from '../../../theme/Colors.es.js';
import '../../../theme/Shadows.es.js';
import '../../../theme/Typography.es.js';
import { boxSizing } from '../../../style-mixins/reset.es.js';
import SvgMiscAi from './compiled/MiscAi.es.js';
import SvgMiscAiFill from './compiled/MiscAiFill.es.js';
import SvgNavAiChatbot from './compiled/NavAiChatbot.es.js';
import SvgMiscEvergreenAi from './compiled/MiscEvergreenAi.es.js';
import SvgMiscMcp from './compiled/MiscMcp.es.js';
import SvgNavMcp from './compiled/NavMcp.es.js';

const sanitizeClassName = (size) => {
  return `${size}`.replace(/[^a-z0-9]/gi, "-");
};
const Styles = labelStyles("Icon", {
  root: (props) => [
    Object.assign(Object.assign({}, boxSizing), { label: `-${props.name}`, display: "inline-block", fill: "currentColor", "> svg": {
      display: "block",
      height: "inherit",
      width: "inherit"
    }, "*": {
      fill: "inherit"
    } }),
    // Though this component doesn't manage active state on its own such that
    // transition styles are necessary, its parent might. When a parent is
    // toggling this component's `color` prop, we need to animate that
    // consistently with our other transitions.
    props.shouldAnimateFill && withModifier("animate", {
      transitionProperty: "fill",
      transitionTimingFunction: Animation.transitionTimingFunction,
      transitionDuration: Animation.transitionDuration
    }),
    !props.canAcceptPointerEvents && withModifier("disable-pointer-events", {
      pointerEvents: "none"
    }),
    props.isBlock && withModifier("block", {
      display: "block"
    }),
    withModifier(sanitizeClassName(`${props.size}x${props.size}`), {
      height: props.size,
      width: props.size
    }),
    props.color && withModifier(props.color, {
      fill: Colors[props.color]
    })
  ]
});
const MissingIcon = () => jsx(
  "svg",
  { "aria-label": "Could not load icon", "data-is-missing-icon": "true", viewBox: "0 0 24 24" },
  jsx("rect", { height: "24", width: "24", x: "0", y: "0" })
);
class IconErrorBoundary extends Component {
  constructor() {
    super(...arguments);
    this.state = {
      didFailToLoad: false
    };
  }
  static getDerivedStateFromError() {
    return {
      didFailToLoad: true
    };
  }
  render() {
    return this.state.didFailToLoad ? jsx(MissingIcon, null) : this.props.children;
  }
}
const Icon = ({ "aria-label": ariaLabel, color, isBlock, name, "aria-hidden": ariaHidden = true, canAcceptPointerEvents = true, shouldAnimateFill = true, size = "small", className }) => {
  const IconComponent = getIcon(name);
  let cssSize = size;
  switch (size) {
    case "small":
      cssSize = "24px";
      break;
    case "medium":
      cssSize = "36px";
      break;
    case "large":
      cssSize = "48px";
      break;
  }
  const stylesOptions = {
    name,
    canAcceptPointerEvents,
    isBlock,
    color,
    shouldAnimateFill,
    size: cssSize
  };
  return jsx(
    "span",
    { "aria-hidden": ariaHidden ? "true" : void 0, "aria-label": ariaLabel, css: Styles.root(stylesOptions), "data-testid": "iconContainer", "data-zds": true, className },
    jsx(
      IconErrorBoundary,
      null,
      jsx(IconComponent, { size: cssSize, color, name })
    )
  );
};
function getIcon(name) {
  try {
    if (name === "miscAI") {
      return SvgMiscAi;
    }
    if (name === "miscAIFill") {
      return SvgMiscAiFill;
    }
    if (name === "navAIChatbot") {
      return SvgNavAiChatbot;
    }
    if (name === "miscEvergreenAI") {
      return SvgMiscEvergreenAi;
    }
    if (name === "miscMCP") {
      return SvgMiscMcp;
    }
    if (name === "navMCP") {
      return SvgNavMcp;
    }
    const capitalizedIcon = `${name[0].toUpperCase()}${name.substring(1)}`;
    return Icons[capitalizedIcon] || MissingIcon;
  } catch (_a) {
    return MissingIcon;
  }
}

export { Icon };
//# sourceMappingURL=Icon.es.js.map
